import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { TreeDeciduous, Calculator, Lock, Loader2, CreditCard, CheckCircle, AlertCircle, XCircle, Download, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Beslutsläge Framework
import { useBeslutslage } from '../../lib/useBeslutslage';
import { generateGallringPremium } from '../../lib/premiumContentGenerators';
import { BESLUTSLAGE_PRICE } from '../../lib/beslutslageConfig';
import { BeslutslageTeaser, SaveBlock } from '../beslutslage';
import BeslutslagePremiumGeneric from '../beslutslage/BeslutslagePremiumGeneric';
import CheckoutLoadingOverlay from '../beslutslage/CheckoutLoadingOverlay';
import { downloadGenericBeslutsbrev, printGenericBeslutsbrev } from '../../lib/beslutsbrevGeneratorGeneric';

// Wartemodus
import { SALES_ENABLED } from '../../lib/featureFlags';

/**
 * GallringsKalkylatorPremium
 * 
 * Gallring calculator WITH Beslutsläge Premium support.
 * Uses the reusable Beslutsläge framework.
 */
const GallringsKalkylatorPremium = () => {
  const { language } = useLanguage();
  
  // Calculator state
  const [bestandsvolym, setBestandsvolym] = useState(200);
  const [uttagProcent, setUttagProcent] = useState(30);
  const [prisPerM3, setPrisPerM3] = useState(800);
  const [gallringskostnad, setGallringskostnad] = useState(280);
  const [result, setResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  // Beslutsläge hook
  const beslutslage = useBeslutslage('gallring', { language });
  
  // Premium content
  const [premiumContent, setPremiumContent] = useState(null);

  // Restore data from return/reopen
  useEffect(() => {
    if (beslutslage.restoredData) {
      const { inputs, result: savedResult } = beslutslage.restoredData;
      if (inputs) {
        setBestandsvolym(inputs.bestandsvolym || 200);
        setUttagProcent(inputs.uttagProcent || 30);
        setPrisPerM3(inputs.prisPerM3 || 800);
        setGallringskostnad(inputs.gallringskostnad || 280);
      }
      if (savedResult) {
        setResult(savedResult);
      }
    }
  }, [beslutslage.restoredData]);

  // Generate premium content when unlocked
  useEffect(() => {
    if (beslutslage.isPremiumUnlocked && result) {
      const content = generateGallringPremium(result, inputs);
      setPremiumContent(content);
    }
  }, [beslutslage.isPremiumUnlocked, result]);

  // Track calculator open on mount
  useEffect(() => {
  }, []); // Only on mount

  const inputs = { bestandsvolym, uttagProcent, prisPerM3, gallringskostnad };

  const content = {
    sv: {
      title: 'Gallringskalkylator',
      description: 'Beräkna resultat från gallring. Uppskatta uttagen volym och nettointäkt baserat på beståndsvolym och gallringsstyrka.',
      standVolume: 'Beståndsvolym (m³/ha)',
      extraction: 'Uttag',
      price: 'Virkespris (kr/m³)',
      cost: 'Gallringskostnad (kr/m³)',
      priceTip: 'Gallringsvirke ger ofta lägre pris',
      costTip: 'Typiskt: 250-350 kr/m³',
      extractionTip: 'Rekommenderat: 25-35% vid förstagallring',
      calculate: 'Beräkna gallring',
      activatePremium: `Aktivera Beslutsläge – ${BESLUTSLAGE_PRICE} kr`,
      processing: 'Förbereder betalning...',
      checkingPayment: 'Vi kontrollerar betalningsstatus...',
      premiumActive: 'Beslutsläge aktiverat',
      premiumActiveDesc: 'Ditt beslutsbrev är tillgängligt',
      loginRequired: 'Logga in för att köpa Beslutsläge',
      paymentCancelled: 'Betalningen avbröts',
      paymentCancelledDesc: 'Du kan fortsätta senare eller försöka igen',
      stripeSecure: 'Säker betalning via Stripe · Engångskostnad per ärende',
      tryAgain: 'Försök igen',
      devUnlock: 'Lås upp (Testläge)',
      devMode: 'Utvecklarläge'
    }
  };

  const c = content[language] || content.sv;

  const calculateThinning = () => {
    const uttagenVolym = bestandsvolym * (uttagProcent / 100);
    const kvarvarandeVolym = bestandsvolym - uttagenVolym;
    const bruttoIntakt = uttagenVolym * prisPerM3;
    const kostnad = uttagenVolym * gallringskostnad;
    const nettoIntakt = bruttoIntakt - kostnad;
    
    setResult({
      uttagenVolym: uttagenVolym.toFixed(1),
      kvarvarandeVolym: kvarvarandeVolym.toFixed(1),
      bruttoIntakt,
      kostnad,
      nettoIntakt
    });
    setIsSaved(false);
    beslutslage.resetPremiumState();
  };

  const handleActivate = () => {
    const calculationData = {
      inputs: { bestandsvolym, uttagProcent, prisPerM3, gallringskostnad },
      result: result
    };
    beslutslage.handleActivatePremium(calculationData);
  };

  const handleDownloadPDF = () => {
    if (premiumContent) {
      downloadGenericBeslutsbrev(premiumContent, {
        calculatorName: 'Gallring',
        arendeText: `Gallring: ${bestandsvolym} m³/ha, uttag ${uttagProcent}%`,
        mainValue: result?.nettoIntakt?.toLocaleString('sv-SE') || '',
        mainUnit: 'kr netto',
        fileName: `Gallring_${bestandsvolym}m3_${uttagProcent}pct`
      });
    }
  };

  // Show payment checking state
  if (beslutslage.isCheckingPayment) {
    return (
      <Card className="w-full">
        <CardContent className="py-12 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-forest-600 mb-4" />
          <p className="text-lg font-medium text-forest-800">{c.checkingPayment}</p>
        </CardContent>
      </Card>
    );
  }

  if (beslutslage.isLoadingCase) {
    return (
      <Card className="w-full">
        <CardContent className="py-12 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-forest-600 mb-4" />
          <p className="text-lg font-medium text-forest-800">Laddar ärende...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="gallring-calculator">
      {beslutslage.isProcessingCheckout && (
        <CheckoutLoadingOverlay language={language} />
      )}
      {/* Payment Success Banner */}
      {beslutslage.showPaymentSuccess && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 relative" data-testid="payment-success-banner">
          <button 
            onClick={() => beslutslage.setShowPaymentSuccess(false)}
            className="absolute top-2 right-2 text-green-600 hover:text-green-800"
            aria-label="Stäng"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-800">Tack för ditt köp</h3>
              <p className="text-sm text-green-700 mt-1">Ditt beslutsbrev är nu tillgängligt. Vi har också skickat en bekräftelse till din e-postadress.</p>
              <p className="text-xs text-green-600 mt-2">Om något inte fungerar, kontakta oss på info@hodema.se.</p>
            </div>
          </div>
        </div>
      )}

      {/* Input Form Card */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-forest-700">
            <TreeDeciduous className="h-5 w-5" />
            {c.title}
          </CardTitle>
          <CardDescription>{c.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{c.standVolume}</Label>
              <Input
                type="number"
                value={bestandsvolym}
                onChange={(e) => setBestandsvolym(Number(e.target.value))}
                min={50}
                data-testid="bestandsvolym-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{c.extraction}: {uttagProcent}%</Label>
              <Slider
                value={[uttagProcent]}
                onValueChange={(v) => setUttagProcent(v[0])}
                min={15}
                max={50}
                step={5}
                className="mt-2"
                data-testid="uttag-slider"
              />
              <p className="text-xs text-muted-foreground">{c.extractionTip}</p>
            </div>
            
            <div className="space-y-2">
              <Label>{c.price}</Label>
              <Input
                type="number"
                value={prisPerM3}
                onChange={(e) => setPrisPerM3(Number(e.target.value))}
                min={0}
                data-testid="pris-input"
              />
              <p className="text-xs text-muted-foreground">{c.priceTip}</p>
            </div>
            
            <div className="space-y-2">
              <Label>{c.cost}</Label>
              <Input
                type="number"
                value={gallringskostnad}
                onChange={(e) => setGallringskostnad(Number(e.target.value))}
                min={0}
                data-testid="kostnad-input"
              />
              <p className="text-xs text-muted-foreground">{c.costTip}</p>
            </div>
          </div>

          <Button 
            onClick={calculateThinning} 
            className="w-full bg-forest-700 hover:bg-forest-800"
            data-testid="calculate-btn"
          >
            <Calculator className="mr-2 h-4 w-4" />
            {c.calculate}
          </Button>
        </CardContent>
      </Card>

      {/* BESLUTSLÄGE RESULTS */}
      {result && (
        <div className="space-y-4" data-testid="beslutslage-results">
          
          {/* Basic Result Card (always free) */}
          <Card className="border-forest-300 bg-gradient-to-br from-forest-50 to-white shadow-sm" data-testid="block-resultat">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-forest-800 text-lg">
                  <TreeDeciduous className="h-5 w-5" />
                  Resultat
                </CardTitle>
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                  Gratis
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-forest-200">
                <div>
                  <p className="text-sm text-muted-foreground">Uttagen volym</p>
                  <p className="text-xl font-bold text-forest-700">{result.uttagenVolym} m³/ha</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Kvarvarande volym</p>
                  <p className="text-xl font-bold text-forest-600">{result.kvarvarandeVolym} m³/ha</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Bruttointäkt</p>
                  <p className="text-lg font-semibold text-forest-700">{result.bruttoIntakt.toLocaleString('sv-SE')} kr</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Kostnad</p>
                  <p className="text-lg font-semibold text-red-600">-{result.kostnad.toLocaleString('sv-SE')} kr</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nettointäkt</p>
                  <p className={`text-lg font-bold ${result.nettoIntakt >= 0 ? 'text-forest-700' : 'text-red-600'}`}>
                    {result.nettoIntakt.toLocaleString('sv-SE')} kr
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* PREMIUM SECTION */}
          {!beslutslage.isPremiumUnlocked ? (
            <>
              {/* Premium Teaser */}
              <BeslutslageTeaser language={language} isComingSoon={false} calculatorType="gallring" isLoggedIn={beslutslage.isLoggedIn} />
              
              {/* Payment Cancelled Notice */}
              {beslutslage.paymentCancelled && (
                <Card className="border-amber-200 bg-amber-50" data-testid="payment-cancelled-card">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">{c.paymentCancelled}</p>
                        <p className="text-sm text-amber-700 mt-1">{c.paymentCancelledDesc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Payment Error */}
              {beslutslage.paymentError && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="py-3">
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">{beslutslage.paymentError}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Payment Button — hidden in maintenance mode */}
              {SALES_ENABLED && (
                <Card className="border-forest-300 bg-gradient-to-br from-forest-50 to-white" data-testid="payment-card">
                  <CardContent className="py-4">
                    {beslutslage.isLoggedIn ? (
                      <Button 
                        onClick={handleActivate}
                        disabled={beslutslage.isProcessingCheckout}
                        className="w-full bg-forest-700 hover:bg-forest-800 text-white h-12 text-base"
                        data-testid="activate-premium-btn"
                      >
                        {beslutslage.isProcessingCheckout ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {c.processing}
                          </>
                        ) : beslutslage.paymentCancelled ? (
                          <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            {c.tryAgain} – {BESLUTSLAGE_PRICE} kr
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            {c.activatePremium}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => {
                          const target = encodeURIComponent('/verktyg?calc=gallring');
                          window.location.href = `/login?redirect=${target}&intent=purchase`;
                        }}
                        className="w-full bg-gray-600 hover:bg-gray-700 text-white"
                        data-testid="login-to-buy-btn"
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        {c.loginRequired}
                      </Button>
                    )}
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      {c.stripeSecure}
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {/* Dev Mode Bypass */}
              {beslutslage.devMode && (
                <Card className="border-dashed border-2 border-amber-300 bg-amber-50/50" data-testid="dev-unlock-card">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-amber-700">
                        <Lock className="h-4 w-4" />
                        <span className="text-sm font-medium">{c.devMode}</span>
                      </div>
                      <Button 
                        onClick={beslutslage.handleDevUnlock}
                        variant="outline"
                        size="sm"
                        className="border-amber-400 text-amber-700 hover:bg-amber-100"
                        data-testid="dev-unlock-btn"
                      >
                        {c.devUnlock}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <>
              {/* Premium Unlocked Header */}
              <Card className="border-green-300 bg-green-50" data-testid="premium-unlocked-card">
                <CardContent className="py-4">
                  <div className="flex flex-col items-center gap-3 text-green-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">{c.premiumActive}</span>
                    </div>
                    <p className="text-sm text-green-600 text-center">{c.premiumActiveDesc}</p>
                    <Button 
                      onClick={handleDownloadPDF}
                      className="bg-green-600 hover:bg-green-700 text-white mt-1"
                      data-testid="quick-pdf-download-btn"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Ladda ner PDF nu
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Premium Content */}
              {premiumContent && (
                <BeslutslagePremiumGeneric 
                  content={premiumContent}
                  calculatorName="Gallring"
                  onDownloadPDF={handleDownloadPDF}
                  onPrint={printGenericBeslutsbrev}
                />
              )}
            </>
          )}
          
          {/* SAVE BLOCK */}
          <SaveBlock 
            language={language}
            isLoggedIn={beslutslage.isLoggedIn}
            onSave={() => setIsSaved(true)}
            onCompare={() => { setResult(null); beslutslage.resetPremiumState(); }}
            isSaved={isSaved}
          />
        </div>
      )}
    </div>
  );
};

export default GallringsKalkylatorPremium;
